import { useMutation } from '@tanstack/react-query';
import { useCallback, useContext, useMemo, useState } from 'react';

import { ServiceFormTemplate, Step } from '@/widgets/ServiceFormTemplate';

import { Payment } from '@/features/ui';
import { AdditionalDocuments } from '@/features/ui/Steps/AdditionalDocuments';
import { additionalDocumentsSchema } from '@/features/ui/Steps/AdditionalDocuments/model/schema';
import { confirmationOfApplicationSchema } from '@/features/ui/Steps/ConfirmationOfApplication/model/schemas';
import { HealthData } from '@/features/ui/Steps/HealthData';
import { healthDataSchema } from '@/features/ui/Steps/HealthData/model/schema';
import { LearningDocument } from '@/features/ui/Steps/LearningDocument/LearningDocument';
import { learningDocumentSchema } from '@/features/ui/Steps/LearningDocument/model/schema';
import { PersonalAndPassportData } from '@/features/ui/Steps/PersonalAndPassportData';
import { personalAndPassportDataSchema } from '@/features/ui/Steps/PersonalAndPassportData/model/schemas';
import { PlaceOfResidence } from '@/features/ui/Steps/PlaceOfResidence';
import { placeOfResidenceSchema } from '@/features/ui/Steps/PlaceOfResidence/model/schemas';
import { SignUp } from '@/features/ui/Steps/SignUp/SignUp';

import { InvoiceStatus } from '@/shared/types';

import { useFormDraftPlainTools } from '@/hooks/useFormDraftPlainTools';
import { usePaymentShortPolling } from '@/hooks/usePaymentShortPolling';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import {
  FormDataType,
  PurchaseItemIdMapping,
  UserRequest,
  UserRequestDraft,
  UserService,
  UserServicePaymentType,
} from '@/api/types';

import api from '@/api';

import {
  disallowedDocumentTypeCodesForAdditionalDocumentStep,
  HEALTH_DOC_STEP_NUMBER,
  MILITARY_STATUSES,
  SERVICE_CODE,
  STEP_NUMBER_PERSONAL_AND_PASSPORT_DATA,
  STEP_NUMBER_SIGN_UP,
  STEP_NUMBER_TO_PAY,
} from './lib/constants';
import { generatePayload } from './lib/generatePayload';
import { objectOfPurchaseSchema, personStatusSchema } from './model/schemas';
import { ServiceProps } from './model/types';
import { WeaponPurchasePermitResult, DataPreview } from './ui/steps';
import { ObjectOfPurchase } from './ui/steps/ObjectOfPurchase';
import { PersonStatus } from './ui/steps/PersonStatus';

export const STEPS: Array<Step<ServiceProps>> = [
  {
    title: 'Мета звернення',
    step: ObjectOfPurchase,
    validationSchema: objectOfPurchaseSchema,
  },
  {
    title: 'Статус особи',
    step: PersonStatus,
    validationSchema: personStatusSchema,
  },
  {
    title: 'Знання матеріальної частини зброї та правил',
    step: LearningDocument,
    validationSchema: learningDocumentSchema,
  },
  { title: 'Стан здоров’я', step: HealthData, validationSchema: healthDataSchema },
  {
    title: 'Особисті та паспортні дані',
    step: PersonalAndPassportData,
    validationSchema: personalAndPassportDataSchema,
  },
  {
    title: 'Поточне місце проживання (зберігання зброї)',
    step: PlaceOfResidence,
    validationSchema: placeOfResidenceSchema,
  },
  { title: 'Додаткові дані', step: AdditionalDocuments, validationSchema: additionalDocumentsSchema },
  { title: 'Підтверження даних', step: DataPreview, validationSchema: confirmationOfApplicationSchema },
  {
    title: 'Підпис',
    step: SignUp,
  },
  { title: 'Оплата', step: Payment },
  { title: 'Результат', step: WeaponPurchasePermitResult },
];

export const WeaponPurchasePermit: React.FC = () => {
  const { getServiceByCode } = useContext(ServicesContext);
  const [currentUserRequest, setCurrentUserRequest] = useState<Nullable<UserRequest>>(null);

  const currentService: UserService = getServiceByCode(SERVICE_CODE) as UserService;
  const serviceFileManager = useMemo(() => new ServiceFileManager(), []);
  const [redirectServiceInfo, setRedirectServiceInfo] = useState<{
    link: string;
    request_id: number;
  }>();

  const { mutateAsync: generatePDF, isPending: isLoadingPdf } = useMutation({
    mutationFn: async ({
      code,
      payload,
    }: {
      code: Parameters<typeof api.services.generatePdf>[0];
      payload: Parameters<typeof api.services.generatePdf>[1];
    }) => {
      const result = await api.services.generatePdf(code, payload);
      serviceFileManager.setAttachment(result);
      return result;
    },
  });

  const { mutateAsync: submitForm, isPending: isLoadingSubmit } = useMutation({
    mutationFn: ({
      code,
      payload,
      allowToProfileUpdate,
    }: {
      code: Parameters<typeof api.services.submit>[0];
      payload: Parameters<typeof api.services.submit>[1];
      allowToProfileUpdate?: string;
    }) => api.services.submit(code, payload, allowToProfileUpdate),
  });

  const { isShortPollingSuccessed, isShortPollingInProgress, isShortPollingFailed, startPaymentShortPolling } =
    usePaymentShortPolling(setCurrentUserRequest);

  const [defaultValues, setDefaultValues] = useState<FormDataType>({});

  const {
    isLoading: isLoadingDraft,
    onDraftCreate,
    onDraftContinue,
    onDraftSave,
    currentRequestId,
    onPaymentContinue,
  } = useFormDraftPlainTools({
    serviceCode: SERVICE_CODE,
    setData: (data) => setDefaultValues(data ?? {}),
    stepNumberToPay: STEP_NUMBER_TO_PAY,
    stepNumberToSignUp: STEP_NUMBER_SIGN_UP,
    steps: STEPS,
  });

  const handleDraftCreate = async (_: any, specificStep?: (step: number) => void): Promise<Nullable<string>> =>
    specificStep &&
    onDraftCreate(
      {
        id: currentService.id,
        name: currentService.name,
        code: SERVICE_CODE,
        totalSteps: STEPS.length,
      },
      specificStep,
    );

  const handleSubmitForm = useCallback(
    async (formData: FormDataType): Promise<void> => {
      const { signedPayload } = await generatePayload({
        requestId: (currentRequestId ?? '') as UserRequestDraft['id'],
        formData,
        service: currentService,
        serviceFileManager,
      });

      await submitForm({
        code: SERVICE_CODE,
        payload: signedPayload,
        allowToProfileUpdate: String(formData.allowProfileUpdate),
      });

      serviceFileManager.download();
      serviceFileManager.clear();
    },
    [currentRequestId, currentService, serviceFileManager, submitForm],
  );

  const handleGeneratePDF = useCallback(
    async (formData: FormDataType): Promise<void> => {
      const { commonPayload } = await generatePayload({
        requestId: (currentRequestId ?? '') as UserRequestDraft['id'],
        formData,
        service: currentService,
      });
      await generatePDF({ code: SERVICE_CODE, payload: commonPayload });
    },
    [currentRequestId, currentService, generatePDF],
  );

  const onGercPaymentContinue = async (userRequestId: UserRequest['id']): Promise<number> => {
    if (sessionStorage.getItem('redirectServiceInfo')) {
      setRedirectServiceInfo(JSON.parse(sessionStorage.getItem('redirectServiceInfo') as string));
      sessionStorage.removeItem('redirectServiceInfo');
    }

    startPaymentShortPolling(userRequestId);

    return onPaymentContinue(userRequestId);
  };

  const isLoading = isLoadingPdf || isLoadingSubmit || isLoadingDraft;

  return (
    <ServiceFormTemplate
      className="cold-steal-transportation"
      serviceCode={SERVICE_CODE}
      title={currentService.name}
      steps={STEPS}
      isLoading={isLoading}
      defaultValues={defaultValues}
      currentRequestId={currentRequestId ?? null}
      onDraftContinue={onDraftContinue}
      onDraftSave={onDraftSave}
      onDraftCreate={handleDraftCreate}
      onPaymentContinue={onPaymentContinue}
      onGercPaymentContinue={onGercPaymentContinue}
      stepProps={{
        handleGeneratePDF,
        submitForm: handleSubmitForm,
        serviceFileManager,
        paymentShortPolling: { isShortPollingInProgress },
        finishedUserRequestStatus: isShortPollingSuccessed || isShortPollingFailed,
        allowedPaymentType: currentService.paymentType ?? UserServicePaymentType.WITHOUT_PAYMENT,
        beforeRedirectToGerc: () => {
          if (redirectServiceInfo) {
            sessionStorage.setItem('redirectServiceInfo', JSON.stringify(redirectServiceInfo));
          }
        },
        userRequestId: currentRequestId ?? currentUserRequest?.id ?? '',
        invoiceStatus: currentUserRequest?.invoice?.status ?? InvoiceStatus.SUCCESS,
        showNpuPaymentInformer: true,
        isHealthDataStepMandatory: false,
        isLearningDocumentStepMandatory: false,
        withAddressEqualityCheck: (formData) => Boolean(formData.hasValidPermit),
        disallowedDocumentTypeCodes: disallowedDocumentTypeCodesForAdditionalDocumentStep,
        learningDocumentCustomNextStepHandler: (formData, specificStep) => async () => {
          if (
            [
              PurchaseItemIdMapping.DEVICE,
              PurchaseItemIdMapping.COLD_WEAPON,
              PurchaseItemIdMapping.HUNTING_RIFLED_WEAPONS,
              PurchaseItemIdMapping.HUNTING_SMOOTHBORE_WEAPONS,
              PurchaseItemIdMapping.BLANK_WEAPON,
              PurchaseItemIdMapping.PNEUMATIC_WEAPONS,
              PurchaseItemIdMapping.GAS_REVOLVERS,
            ].includes(formData.objectOfPurchase?.id ?? '') &&
            !formData.hasValidPermit
          ) {
            if (!MILITARY_STATUSES.includes(formData.personStatus?.id ?? '')) {
              specificStep(HEALTH_DOC_STEP_NUMBER);
              return;
            }
            if (!Number(formData.firearmRegistrationDoc?.id)) {
              STEPS[HEALTH_DOC_STEP_NUMBER - 1].skipped = true;
              specificStep(STEP_NUMBER_PERSONAL_AND_PASSPORT_DATA);
              return;
            }
          }
        },
      }}
    />
  );
};
